import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Ticket, Info, Share2, User, Loader2, Clock, Globe } from 'lucide-react';
import { fetchEvents } from '../api/api';

const EventDetailScreen = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [event, setEvent] = useState(location.state?.event || null);
    const [loading, setLoading] = useState(!event);

    useEffect(() => {
        const loadEvent = async () => {
            try {
                const events = await fetchEvents();
                if (events && events.length > 0) {
                    const targetEvent = id
                        ? events.find(e => e.id.toString() === id.toString())
                        : events[0];

                    if (targetEvent) {
                        setEvent(targetEvent);
                    }
                }
            } catch (error) {
                console.error("Failed to load event data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (!event || (id && event.id.toString() !== id.toString())) {
            loadEvent();
        }
    }, [id, event]);

    // --- HELPER FUNCTIONS ADDED HERE ---
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        // Append T00:00:00 to avoid timezone shifts changing the day
        const date = new Date(dateStr + "T00:00:00"); 
        if (isNaN(date.getTime())) return dateStr; 
        
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
        const year = date.getFullYear();
        
        return `${day} ${month}. ${year}`;
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '8:00 PM'; // Default fallback
        const date = new Date(`1970-01-01T${timeStr}`);
        if (isNaN(date.getTime())) return timeStr;
        
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };
    // -----------------------------------

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream">
                <Loader2 className="w-12 h-12 text-primary animate-spin" />
            </div>
        );
    }

    if (!event) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-cream p-6 text-center">
                <h2 className="text-3xl font-playfair font-black text-secondary mb-4">Experience Not Found</h2>
                <p className="text-secondary/50 mb-8 max-w-md">We couldn't locate the specific divine journey you are seeking.</p>
                <button
                    onClick={() => navigate('/')}
                    className="px-10 py-4 bg-primary text-white rounded-2xl font-bold hover:bg-secondary transition-all shadow-lg shadow-primary/20"
                >
                    Return to Home
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream font-outfit text-secondary pb-24">
            {/* Navbar for context */}
            <nav className="bg-white/70 backdrop-blur-md border-b border-primary/10 px-6 py-4 mb-8">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div onClick={() => navigate('/')} className="flex items-center gap-3 cursor-pointer">
                        <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                            <Ticket className="text-white" size={16} />
                        </div>
                        <span className="text-lg font-bold tracking-tight">GO<span className="text-primary">BACK</span></span>
                    </div>
                    <button onClick={() => navigate('/')} className="text-sm font-bold text-primary hover:text-secondary transition-colors">
                        Back to Shows
                    </button>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Left Column: Image and quick info */}
                    <div className="lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-white p-4 rounded-[3rem] shadow-xl shadow-primary/5 border border-primary/10 sticky top-28"
                        >
                            <div className="relative overflow-hidden rounded-[2.5rem]">
                                <img
                                    src={event.image_url}
                                    alt={event.title}
                                    className="w-full aspect-[4/5] object-cover"
                                    onError={(e) => {
                                        e.target.src = 'https://images.unsplash.com/photo-1590766940554-634a7ed41450?w=800&q=80';
                                    }}
                                />
                                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm px-4 py-1.5 rounded-full text-[10px] font-black text-primary uppercase tracking-widest shadow-sm">
                                    {event.category || 'Divine Journey'}
                                </div>
                            </div>

                            <div className="mt-8 grid grid-cols-2 gap-4">
                                <div className="bg-cream p-5 rounded-3xl border border-primary/5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Calendar size={14} className="text-primary" />
                                        <p className="text-[10px] text-secondary/40 font-black uppercase tracking-widest">Date</p>
                                    </div>
                                    <p className="font-bold text-secondary">
                                        {formatDate(event.date)}
                                    </p>
                                </div>
                                <div className="bg-cream p-5 rounded-3xl border border-primary/5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Clock size={14} className="text-primary" />
                                        <p className="text-[10px] text-secondary/40 font-black uppercase tracking-widest">Time</p>
                                    </div>
                                    <p className="font-bold text-secondary">
                                        {formatTime(event.start_time || event.time)}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column: Details */}
                    <div className="lg:col-span-7">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                            <div className="flex justify-between items-center mb-6">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-xs font-bold uppercase tracking-widest border border-primary/20">
                                    Featured Show
                                </div>
                                <button className="w-10 h-10 rounded-full bg-white border border-primary/10 flex items-center justify-center text-secondary/40 hover:text-primary transition-all shadow-sm">
                                    <Share2 size={18} />
                                </button>
                            </div>

                            <h1 className="text-5xl md:text-7xl font-playfair font-black text-secondary leading-tight mb-6">
                                {event.title}
                            </h1>

                            <div className="flex items-center text-secondary/70 mb-10 bg-white w-fit px-6 py-3 rounded-2xl border border-primary/10 shadow-sm">
                                <MapPin size={20} className="text-primary mr-3" />
                                <span className="font-bold">{event.location}</span>
                            </div>

                            <div className="space-y-10">
                                <section>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="h-6 w-1 bg-primary rounded-full"></div>
                                        <h2 className="text-xl font-bold text-secondary flex items-center">
                                            About the Performance
                                        </h2>
                                    </div>
                                    <div className="bg-white rounded-[2.5rem] p-8 border border-primary/5 shadow-sm">
                                        <p className="text-secondary/70 leading-relaxed font-medium text-lg italic">
                                            "{event.description || 'Join us for a soul-stirring experience that celebrates our rich cultural heritage. A performance that promises to transport you to a world of peace and divinity.'}"
                                        </p>
                                    </div>
                                </section>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="flex items-center gap-5 p-6 bg-white rounded-3xl border border-primary/5 shadow-sm">
                                        <div className="w-14 h-14 bg-primary/10 text-primary rounded-2xl flex items-center justify-center">
                                            <User size={28} />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-secondary/40 font-black uppercase tracking-widest mb-1">Organized By</p>
                                            <p className="font-bold text-secondary">{event.organizer || 'Rotaract Bhavnagar Youth'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-5 p-6 bg-white rounded-3xl border border-primary/5 shadow-sm">
                                        <div className="w-14 h-14 bg-primary/10 text-primary rounded-2xl flex items-center justify-center">
                                            <Globe size={28} />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-secondary/40 font-black uppercase tracking-widest mb-1">Powered by</p>
                                            <p className="font-bold text-secondary">Leeway Softech</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Call to Action Card */}
                                <div className="bg-secondary rounded-[3rem] p-10 flex flex-col md:flex-row items-center justify-between gap-8 shadow-2xl relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary opacity-10 rounded-full translate-x-1/2 -translate-y-1/2 blur-2xl"></div>

                                    <div className="text-center md:text-left relative z-10">
                                        <p className="text-primary text-xs font-black uppercase tracking-[0.2em] mb-2">Starting From</p>
                                        <div className="flex flex-col">
                                            <span className="text-4xl font-black text-white">₹200</span>
                                            <span className="text-white/40 text-xs font-bold mt-1">Inclusive of all taxes</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => navigate(`/book/${event.id}`, { state: { event } })}
                                        className="w-full md:w-auto px-12 py-5 bg-primary text-white rounded-[1.5rem] font-black text-xl hover:bg-white hover:text-secondary transition-all flex items-center justify-center gap-3 shadow-xl shadow-primary/20 transform hover:-translate-y-1 active:scale-95 z-10"
                                    >
                                        <Ticket size={24} /> Book Tickets
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default EventDetailScreen;